import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Bookmark, Share2, Loader2, X, Check } from 'lucide-react';

export default function AffirmationCard() {
  const [aff, setAff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savedList, setSavedList] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    http.get('/affirmations/today')
      .then((r) => setAff(r.data))
      .catch(() => setAff(null))
      .finally(() => setLoading(false));
  }, []);

  const toggleSave = async () => {
    if (!aff || saving) return;
    setSaving(true);
    try {
      const r = await http.post(`/affirmations/${aff.id}/save`);
      setAff((a) => ({ ...a, saved: r.data.saved }));
      toast.success(r.data.saved ? 'Saved to your collection' : 'Removed from your collection');
      if (savedList) setSavedList(null); // force refresh next open
    } catch { toast.error('Could not update. Try again.'); }
    finally { setSaving(false); }
  };

  const share = async () => {
    if (!aff || sharing) return;
    setSharing(true);
    try {
      await http.post(`/affirmations/${aff.id}/share`);
      setAff((a) => ({ ...a, shared: true }));
      toast.success('Shared to the Brotherhood board');
    } catch (e) {
      if (e?.response?.status === 428) {
        toast.error('Accept the community guidelines first.', {
          action: { label: 'Open Community', onClick: () => navigate('/app/community') },
        });
      } else { toast.error('Could not share. Try again.'); }
    } finally { setSharing(false); }
  };

  const openSaved = async () => {
    setShowSaved(true);
    if (savedList) return;
    try {
      const r = await http.get('/affirmations/saved');
      setSavedList(r.data);
    } catch { setSavedList([]); }
  };

  const unsaveFromList = async (id) => {
    try {
      await http.post(`/affirmations/${id}/save`);
      setSavedList((list) => (list || []).filter((x) => x.id !== id));
      if (aff?.id === id) setAff((a) => ({ ...a, saved: false }));
    } catch { toast.error('Could not update.'); }
  };

  if (loading) {
    return (
      <section data-testid="home-affirmation-loading"
        className="rounded-[28px] p-7 lg:p-9 flex items-center justify-center" style={{ backgroundColor: '#0B1726' }}>
        <Loader2 className="animate-spin text-gold" size={20}/>
      </section>
    );
  }
  if (!aff) return null;

  return (
    <>
      <section data-testid="home-affirmation" className="relative rounded-[28px] overflow-hidden animate-fade-up"
        style={{ backgroundColor: '#0B1726' }}>
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(600px 240px at 88% 0%, rgba(197,160,89,0.22), transparent 60%), radial-gradient(520px 260px at 0% 100%, rgba(90,110,138,0.16), transparent 55%)' }}/>
        <div className="relative p-7 lg:p-10 text-white">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold inline-flex items-center gap-2">
              <Sparkles size={13} strokeWidth={2}/> Today&rsquo;s affirmation
            </p>
            <button type="button" data-testid="affirmation-view-saved" onClick={openSaved}
              className="text-[11px] font-semibold text-white/60 hover:text-white transition-smooth inline-flex items-center gap-1">
              <Bookmark size={12}/> Saved
            </button>
          </div>
          <p className={`font-heading text-xl lg:text-2xl mt-4 leading-relaxed max-w-3xl ${expanded ? '' : 'line-clamp-4'}`}>
            &ldquo;{aff.text}&rdquo;
          </p>
          {aff.text && aff.text.length > 180 && (
            <button type="button" data-testid="affirmation-readmore" onClick={() => setExpanded(v => !v)}
              className="text-[12px] font-semibold text-gold hover:text-white transition-smooth mt-2">
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
          <div className="mt-6 flex items-center gap-3">
            <button type="button" data-testid="affirmation-save" onClick={toggleSave} disabled={saving}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-smooth ${aff.saved ? 'bg-gold text-navy' : 'bg-white/10 text-white hover:bg-white/15 border border-white/15'}`}>
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Bookmark size={14} fill={aff.saved ? 'currentColor' : 'none'}/>}
              {aff.saved ? 'Saved' : 'Save'}
            </button>
            <button type="button" data-testid="affirmation-share" onClick={share} disabled={sharing || aff.shared}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-smooth ${aff.shared ? 'bg-white/5 text-white/50 cursor-default' : 'bg-white/10 text-white hover:bg-white/15 border border-white/15'}`}>
              {sharing ? <Loader2 size={14} className="animate-spin"/> : aff.shared ? <Check size={14}/> : <Share2 size={14}/>}
              {aff.shared ? 'Shared' : 'Share'}
            </button>
          </div>
          <p className="text-[11px] text-white/40 mt-4">Refreshes each day, written for you by your Coach.</p>
        </div>
      </section>

      {showSaved && (
        <div data-testid="affirmation-saved-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setShowSaved(false)}>
          <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/5">
              <h3 className="font-heading text-xl text-navy">Your saved affirmations</h3>
              <button type="button" data-testid="affirmation-saved-close" onClick={() => setShowSaved(false)}
                className="text-navy/40 hover:text-navy transition-smooth"><X size={20}/></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {savedList === null && <div className="py-10 text-center"><Loader2 className="animate-spin inline text-navy/40" size={20}/></div>}
              {savedList && savedList.length === 0 && (
                <div className="py-12 text-center" data-testid="affirmation-saved-empty">
                  <Bookmark className="mx-auto text-navy/20" size={28}/>
                  <p className="font-heading text-lg text-navy mt-3">No saved affirmations yet.</p>
                  <p className="text-sm text-navy/50 mt-1">Tap Save on one you love to keep it here.</p>
                </div>
              )}
              {savedList && savedList.map((s) => (
                <div key={s.id} data-testid={`affirmation-saved-item-${s.id}`}
                  className="rounded-2xl bg-beige border border-navy/5 p-4 flex items-start gap-3">
                  <p className="flex-1 text-navy leading-snug">&ldquo;{s.text}&rdquo;</p>
                  <button type="button" data-testid={`affirmation-unsave-${s.id}`} onClick={() => unsaveFromList(s.id)}
                    className="text-gold shrink-0" title="Remove from collection">
                    <Bookmark size={18} fill="currentColor"/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
